import { auth } from '@/api/firebase';
import axios from 'axios';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useMainContext } from '@/context/main-context';
import { useLogs } from '@/hooks/useLogs';
import { formatJSONDate } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';
import PhoneNumberInput from '../ui/phone-number-input';

const AddTeacherForm = () => {
  const { activeBranchId } = useMainContext();
  const { addLog } = useLogs();
  const [debouncedFullName, setDebouncedFullName] = React.useState('');
  const [isVisible, setIsVisible] = React.useState(false);
  const toggleVisibility = () => setIsVisible((prevState) => !prevState);

  const { toast } = useToast();

  const defaultValue = {
    email: '',
    password: '',
    fullName: '',
    phone: '',
    address: '',
    position: '',
    salaryType: 'fixed',
    salaryAmount: 0,
    percentRate: 0,
    isTeacherUpdate: false,
  };

  const {
    control,
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({ defaultValues: defaultValue });

  const fullName = watch('fullName');
  const bornDate = watch('bornDate');
  const salaryType = watch('salaryType');

  const getYearFromDate = (date) => {
    if (!date) return '';
    return formatJSONDate(date).split('.').pop();
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFullName(fullName);
    }, 300);

    return () => clearTimeout(timer);
  }, [fullName]);

  React.useEffect(() => {
    if (debouncedFullName) {
      const birthYear = getYearFromDate(bornDate);
      const username =
        debouncedFullName.replace(/\s+/g, '').toLowerCase() + birthYear;
      const password = debouncedFullName.replace(/\s+/g, '').toLowerCase();
      setValue('email', username);
      setValue('password', password);
    }
  }, [debouncedFullName, bornDate, setValue]);

  const onSubmit = async (data) => {
    try {
      const loadingToast = toast({
        title: "O'qituvchi qo'shilmoqda, Iltimos kuting...",
        variant: 'loading',
      });

      const url = 'https://studypro-server.vercel.app/add-teacher';

      const payload = {
        email: `${data.email}@teacher.uz`,
        password: data.password,
        fullName: data.fullName,
        phone: data.phone,
        address: data.address,
        position: data.position,
        role: auth.currentUser?.uid,
        salaryType: data.salaryType || 'fixed',
        salaryAmount: parseFloat(data.salaryAmount) || 0, // 🔑
        percentRate: parseFloat(data.percentRate) || 0, // 🔑
        isTeacherUpdate: false,
        branchId: activeBranchId,
      };

      const response = await axios.post(url, payload, {
        timeout: 10000,
      });

      if (response.status === 201) {
        reset();

        addLog('Created', "O'qituvchi", 'Yangi o‘qituvchi qo‘shildi', {
          teacherData: payload,
        }).catch((err) => {
          console.error('Log yozishda xato:', err);
        });

        toast({
          id: loadingToast.id,
          title: `${data.fullName} tizimga qo‘shildi`,
          variant: 'default',
        });
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.errorInfo?.message ||
        error.message ||
        'Kutilmagan xatolik yuz berdi.';

      toast({
        title: 'Xatolik',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Ism va Yo'nalish */}
      <div className="flex flex-col md:flex-row items-start gap-2">
        <div className="w-full">
          <Label required htmlFor="fullName">
            Ism Familiya
          </Label>
          <Input
            disabled={isSubmitting}
            type="text"
            id="fullName"
            placeholder="John Doe"
            {...register('fullName', {
              required: "Bu yerni to'ldirish talab qilinadi",
            })}
          />
          <small className="text-xs md:text-sm text-red-500">
            {errors.fullName?.message}
          </small>
        </div>

        <div className="w-full">
          <Label required htmlFor="position">
            Yo'nalish
          </Label>
          <Input
            disabled={isSubmitting}
            type="text"
            id="position"
            placeholder="Frontend Developer"
            {...register('position', {
              required: "Bu yerni to'ldirish talab qilinadi",
            })}
          />
          <small className="text-xs md:text-sm text-red-500">
            {errors.position?.message}
          </small>
        </div>
      </div>

      {/* Telefon va Manzil */}
      <div className="flex flex-col md:flex-row items-start gap-2">
        <div className="w-full">
          <Label required htmlFor="phone">
            Telefon raqam
          </Label>
          <Controller
            name="phone"
            control={control}
            defaultValue=""
            rules={{
              required: "Bu yerni to'ldirish talab qilinadi",
              minLength: { value: 10, message: "Yetarlicha raqam qo'shilmadi" },
            }}
            render={({ field }) => (
              <PhoneNumberInput
                id="phone"
                disabled={isSubmitting}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <small className="text-xs md:text-sm text-red-500">
            {errors.phone?.message}
          </small>
        </div>

        <div className="w-full">
          <Label optional htmlFor="address">
            Manzil
          </Label>
          <Input
            disabled={isSubmitting}
            type="text"
            id="address"
            placeholder="Toshkent, Yunusobod tumani"
            {...register('address')}
          />
          <small className="text-xs md:text-sm text-red-500">
            {errors.address?.message}
          </small>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-2">
        {/* SalaryType select */}
        <div className="w-full md:col-span-2">
          <Label required>Oylik turi</Label>
          <Controller
            name="salaryType"
            control={control}
            rules={{ required: "Bu yerni to'ldirish talab qilinadi" }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Oylik turini tanlang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed maosh</SelectItem>
                  <SelectItem value="percent">
                    Talabalardan to‘langan kurs narxidan foiz
                  </SelectItem>
                  <SelectItem value="hybrid">Kombinatsiya (hybrid)</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {errors.salaryType?.message && (
            <small className="text-xs text-red-500">
              {errors.salaryType?.message}
            </small>
          )}

          {salaryType === 'fixed' && (
            <small className="text-xs text-gray-500">
              O‘qituvchi belgilangan oylik maosh oladi.
            </small>
          )}

          {salaryType === 'percent' && (
            <small className="text-xs text-gray-500">
              O‘qituvchi talabalardan to‘langan kurs narxidan ma’lum foiz
              asosida maosh oladi.
            </small>
          )}

          {salaryType === 'hybrid' && (
            <small className="text-xs text-gray-500">
              O‘qituvchi belgilangan oylik + qo‘shimcha foiz asosida maosh
              oladi.
            </small>
          )}
        </div>

        {/* Fixed */}
        {salaryType === 'fixed' && (
          <div>
            <Label required>Oylik miqdori</Label>
            <Controller
              name="salaryAmount"
              control={control}
              rules={{ required: "Bu yerni to'ldirish talab qilinadi" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Masalan: 7 000 000 so'm"
                  value={
                    field.value
                      ? new Intl.NumberFormat('uz-UZ')
                          .format(field.value)
                          .replace(/\u202F/g, ' ')
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, ''); // faqat raqamlar
                    field.onChange(raw ? Number(raw) : '');
                  }}
                />
              )}
            />
          </div>
        )}

        {/* Percent */}
        {salaryType === 'percent' && (
          <div>
            <Label required>Foiz miqdori (%)</Label>
            <Input
              type="number"
              placeholder="Masalan: 30% (talabalardan to‘langan kurs narxidan)"
              {...register('percentRate', {
                required: "Bu yerni to'ldirish talab qilinadi",
              })}
            />
          </div>
        )}

        {/* Hybrid */}
        {salaryType === 'hybrid' && (
          <>
            <div>
              <Label required>Fixed maosh</Label>
              <Controller
                name="salaryAmount"
                control={control}
                rules={{ required: "Bu yerni to'ldirish talab qilinadi" }}
                render={({ field }) => (
                  <Input
                    type="text"
                    placeholder="Masalan: 7 000 000 so'm"
                    value={
                      field.value
                        ? new Intl.NumberFormat('uz-UZ')
                            .format(field.value)
                            .replace(/\u202F/g, ' ')
                        : ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, ''); // faqat raqamlar
                      field.onChange(raw ? Number(raw) : '');
                    }}
                  />
                )}
              />
            </div>
            <div>
              <Label required>Foiz (%)</Label>
              <Input
                type="number"
                placeholder="Masalan: 20% (talabalardan to‘langan kurs narxidan)"
                {...register('percentRate', {
                  required: "Bu yerni to'ldirish talab qilinadi",
                })}
              />
            </div>
          </>
        )}
      </div>

      {/* Username va Password */}
      <div className="flex flex-col md:flex-row items-start gap-2">
        <div className="w-full">
          <Label required htmlFor="email">
            Username
          </Label>
          <div className="relative">
            <Input
              disabled={isSubmitting}
              type="text"
              id="email"
              placeholder="johndoe"
              className="peer pr-12"
              {...register('email', {
                required: "Bu yerni to'ldirish talab qilinadi",
              })}
            />
            <span className="absolute inset-y-0 right-0 flex items-center justify-center pr-3 text-sm text-muted-foreground peer-disabled:opacity-50">
              @teacher.uz
            </span>
          </div>
          <small className="text-xs md:text-sm text-red-500">
            {errors.email?.message}
          </small>
        </div>

        <div className="w-full">
          <Label required htmlFor="password">
            Password
          </Label>
          <div className="relative">
            <Input
              disabled={isSubmitting}
              type={isVisible ? 'text' : 'password'}
              id="password"
              placeholder="**********"
              {...register('password', {
                required: "Bu yerni to'ldirish talab qilinadi",
              })}
            />
            <button
              type="button"
              onClick={toggleVisibility}
              className="absolute inset-y-0 right-0 flex items-center justify-center w-9 text-muted-foreground/80 hover:text-foreground"
              aria-label={isVisible ? 'Hide password' : 'Show password'}
            >
              {isVisible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <small className="text-xs md:text-sm text-red-500">
            {errors.password?.message}
          </small>
        </div>
      </div>

      <Button disabled={isSubmitting} type="submit" variant="default">
        {!isSubmitting ? "Qo'shish" : "Qo'shilmoqda..."}
      </Button>
    </form>
  );
};

export default AddTeacherForm;
